import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useXP } from '../contexts/XPContext';

const XPNotification = () => {
    const { pendingNotifications, dismissNotification } = useXP();
    const [visible, setVisible] = useState(false);
    const [currentNotification, setCurrentNotification] = useState(null);

    useEffect(() => {
        if (pendingNotifications.length > 0 && !currentNotification) {
            // Show the first notification in queue
            setCurrentNotification(pendingNotifications[0]);
            setVisible(true);

            // Auto-dismiss after 4 seconds
            const timer = setTimeout(() => {
                setVisible(false);
                // Wait for exit animation before clearing
                setTimeout(() => {
                    setCurrentNotification(null);
                    dismissNotification();
                }, 300);
            }, 4000);

            return () => clearTimeout(timer);
        }
    }, [pendingNotifications, currentNotification, dismissNotification]);

    if (!currentNotification) return null;

    const { xp_awarded, leveled_up, old_level, new_level, level_title } = currentNotification;

    const styles = {
        container: {
            position: 'fixed',
            top: 'var(--space-xl)',
            right: 'var(--space-xl)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
            padding: 'var(--space-md) var(--space-lg)',
            background: 'var(--gradient-warm)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 10px 40px rgba(245, 158, 11, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            transform: visible ? 'translateX(0)' : 'translateX(120%)',
            opacity: visible ? 1 : 0,
            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            cursor: 'pointer',
        },
        iconContainer: {
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 'var(--radius-md)',
            fontSize: '1.5rem',
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-xs)',
        },
        xpAmount: {
            fontWeight: 700,
            fontSize: '1.25rem',
            color: 'white',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
        },
        subtitle: {
            fontSize: '0.875rem',
            color: 'rgba(255, 255, 255, 0.9)',
        },
        levelUpBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-xs)',
            padding: 'var(--space-xs) var(--space-sm)',
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: 'var(--radius-full)',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'white',
            marginTop: 'var(--space-xs)',
        },
    };

    const handleClick = () => {
        setVisible(false);
        setTimeout(() => {
            setCurrentNotification(null);
            dismissNotification();
        }, 300);
    };

    return createPortal(
        <div style={styles.container} onClick={handleClick}>
            <div style={styles.iconContainer}>
                {leveled_up ? '🎉' : '✨'}
            </div>
            <div style={styles.content}>
                <span style={styles.xpAmount}>+{xp_awarded} XP</span>
                {leveled_up ? (
                    <>
                        <span style={styles.subtitle}>Level Up!</span>
                        <span style={styles.levelUpBadge}>
                            Level {old_level} → Level {new_level}
                            <span style={{ marginLeft: '4px' }}>{level_title}</span>
                        </span>
                    </>
                ) : (
                    <span style={styles.subtitle}>Keep going!</span>
                )}
            </div>
        </div>,
        document.body
    );
};

export default XPNotification;
